import React, { memo, useCallback, useState } from 'react'

import {
  AIGenerated2,
  Button,
  Icon,
  Verified,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func } from 'prop-types'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'

import { NOOP } from 'Utils/stableDefaults'

import AccessCodeForm from '../AccessCodeForm/AccessCodeForm'

const WAITLIST_PATH = '/api/internal/generator/waitlist'

const WAITLIST_ERROR_MESSAGE = 'Could not join the waitlist. Please try again.'

// Free-beta card shared by the 3D Studio usage modal and the AI subscription landing
// waitlist modal. The component owns the join POST and the access-code reveal; callers
// supply onJoined (apply the result -- the studio writes the new tier to its store, the
// landing swaps to its confirmation view) and onCodeApplied (forwarded to AccessCodeForm).
function WaitlistCard({ onJoined = NOOP, onCodeApplied = NOOP, onClose: handleClose }) {
  const { postData: postWaitlist, isLoading: isJoining } = useApiPost()
  const [isCodeFormOpen, setIsCodeFormOpen] = useState(false)

  const handleJoin = useCallback(() => {
    if (isJoining) {
      return
    }

    postWaitlist({
      path: WAITLIST_PATH,
      data: {},
      onSuccess: (response) => {
        const data = response?.data?.data || {}

        onJoined(data)
      },
      onError: () => {
        window.showNotification(WAITLIST_ERROR_MESSAGE, { type: 'error', autohide: true })
      },
    })
  }, [postWaitlist, isJoining, onJoined])

  const handleToggleCodeForm = useCallback(() => {
    setIsCodeFormOpen(prev => !prev)
  }, [])

  return (
    <section className="usage-modal__card usage-modal__card--beta">
      <div className="usage-modal__card-chip cgt-caption-semibold usage-modal__card-chip--beta">
        Free beta
      </div>

      <div className="usage-modal__price">
        <span className="cgt-heading-300">$0</span>
      </div>

      <ul className="usage-modal__bullets">
        <li className="usage-modal__bullet">
          <Icon
            svgComponent={Verified}
            color="primary"
            size="small"
            notSpaced
            className="usage-modal__bullet-icon"
          />
          <span className="cgt-body-base">
            <span className="cgt-body-md-semibold">5 Free Generations</span>
            {' in CGTrader AI'}
          </span>
        </li>
        <li className="usage-modal__bullet">
          <Icon
            svgComponent={Verified}
            color="primary"
            size="small"
            notSpaced
            className="usage-modal__bullet-icon"
          />
          <span className="cgt-body-base">No credit card is required</span>
        </li>
        <li className="usage-modal__bullet-divider" aria-hidden="true" />
        <li className="usage-modal__bullet">
          <Icon
            svgComponent={AIGenerated2}
            color="primary"
            size="small"
            notSpaced
            className="usage-modal__bullet-icon"
          />
          <span className="cgt-body-base">Great for getting started and trying things out</span>
        </li>
      </ul>

      <div className="usage-modal__card-footer">
        <Button
          size="small"
          type="secondary"
          onClick={handleJoin}
          disabled={isJoining}
        >
          {isJoining ? 'Joining...' : 'Join the Waitlist'}
        </Button>
        <Button
          size="small"
          type="ghost"
          onClick={handleToggleCodeForm}
        >
          I have access code
        </Button>
      </div>

      {isCodeFormOpen && <AccessCodeForm onApplied={onCodeApplied} onClose={handleClose} />}
    </section>
  )
}

WaitlistCard.propTypes = {
  onJoined: func,
  onCodeApplied: func,
  onClose: func.isRequired,
}

export default memo(WaitlistCard)
